import participantStatus from "enums/participantStatus";
import Promise from "bluebird";
import ko from "knockout";
import _ from "lodash";
import Call from "models/Call";
import apiCore from "services/apiCore";
import communicationService from "services/communicationService";
import LOG from "services/loggingService";
import toasts from "services/toastService";
import PubSub from "util/PubSub";
import { withRequestRetry } from "util/withRequestRetry";
import errorCallbackFactory from "util/errorCallbackFactory";

const activeCalls = ko.observableArray([]);

const MAKE_CALL_NOTIFICATION_TOPIC = "callService.notifications.makeCall";

const CALL_TYPES = {
    MONITOR: "monitor",
    COACH: "coach",
    BARGE: "barge",
};

const MAX_RETRIES = 10;

/**
 * Call service. Handles call retrieval, updates & subscriptions.
 * @exports services/callService
 */
const callService = {
    uriBuilder: communicationService.createUriBuilder("/conversations/calls"),

    // ===============================================================================
    // Properties
    // ===============================================================================

    /**
     * Observable array of all active calls
     */
    activeCalls: activeCalls,

    /**
     * Observable array of all active user calls. This is a subset of activeCalls.
     */
    activeUserCalls: ko.computed(function () {
        return _.filter(activeCalls(), function (call) {
            return call.isUserCall();
        });
    }),

    /**
     * Observable array of all active agent calls. This is a subset of activeCalls.
     */
    activeAgentCalls: ko.computed(function () {
        return _.filter(activeCalls(), function (call) {
            return call.isAgentCall();
        });
    }),

    // ===============================================================================
    // Methods
    // ===============================================================================

    getCall: function (callId, options) {
        const opts = _.assign(
            {
                shouldReturnRawData: false,
            },
            options
        );

        return communicationService
            .getCommunication(this.uriBuilder, callId)
            .then(function (callData) {
                if (opts.shouldReturnRawData) {
                    return callData;
                }

                return new Call(callData);
            });
    },

    /**
     * Get the calls for a current user
     * @return {Promise}
     */
    getActiveCalls: function () {
        return apiCore.v2.get(
            callService.uriBuilder.communicationsByType("call")
        );
    },

    /**
     * Monitor a participant on a call
     * @param {string} id the id of the call to monitor.
     * @param {string} participantId the id of the participant to monitor.
     * @param {Boolean} notifySubscribers indicates if subscribes to new answered calls should be notified
     *                  of the newly monitored call.
     *
     * @return {promise} a promise that resolves with the {@link Call} object
     */
    monitorCall: function (id, participantId, notifySubscribers) {
        const self = this;

        // This is a call that the current user is currently not a participant of.  So, we have to request
        // to be added as a monitor and then wait for the new call to show up in the active calls list, and then
        // verify that the current user's monitor attribute is set to true.
        return apiCore.v2
            .post(callService.uriBuilder.monitor(id, participantId), null, null)
            .then(function () {
                return waitForNewCallToBeCreated(id, CALL_TYPES.MONITOR);
            })
            .then(function (call) {
                return communicationService.validateActionSucceeded(
                    call.id,
                    call.participantForCurrentUser().id,
                    Call,
                    "status",
                    participantStatus.CONNECTED,
                    self.activeCalls,
                    self.uriBuilder
                );
            })
            .then(function (call) {
                if (notifySubscribers) {
                    PubSub.Topic(MAKE_CALL_NOTIFICATION_TOPIC).publish(id);
                }
                return call;
            })
            .catch(function (err) {
                LOG.error("Error while monitoring call:", err);
                let errorKey = "phone.monitorCall.error.generic";
                if (
                    err.details &&
                    err.details.response &&
                    err.details.response.code
                ) {
                    const errorKeyWithCode =
                        "phone.monitorCall.error." + err.details.response.code;
                    if (ko.i18n.keyExists(errorKeyWithCode)) {
                        errorKey = errorKeyWithCode;
                    }
                }
                toasts.notifyMajorAlert(errorKey);
            });
    },

    /**
     * Create a new call for the current user
     * @param {Object} options the call options object that can have the following properties:
     *              call - the phone number/address to call
     *              callUserId - the id of the user to call
     *              callQueueId - the id of the queue to call
     *              callFrom - the id of the queue to call on behalf of
     * @param {Boolean} notifySubscribers indicates if subscribes to new answered calls should be notified
     *                  of the newly monitored call.
     * @return {promise} a promise that resolves with the {@link Call} object
     */
    createCall: function (options, notifySubscribers) {
        return apiCore.v2
            .post(callService.uriBuilder.base(), null, options)
            .then(function (callData) {
                return waitForNewCallToBeCreated(callData.id).then(function (
                    call
                ) {
                    if (notifySubscribers) {
                        PubSub.Topic(MAKE_CALL_NOTIFICATION_TOPIC).publish(
                            call.id
                        );
                    }
                    return call;
                });
            });
    },

    /**
     * Creates a new call from the current user to the
     * provided destination address.
     * @param {string} destAddress the telephony address to call
     * @param {Boolean} notifySubscribers indicates if subscribes to new answered calls should be notified
     *                  of the newly monitored call.
     * @return {promise} a promise that resolves with the {@link Call} object
     */
    makeCall: function (destAddress, notifySubscribers) {
        return callService.createCall({ call: destAddress }, notifySubscribers);
    },

    /**
     * Ends an ongoing call.
     * @param {string} callId the id of the call to end.
     * @param {string} participant the id of the participent to disconnect from the call.
     * @return {promise} a promise that resolves with the {@link Call} object
     */
    endCall: function (id, participantId) {
        return communicationService.disconnect(
            this.uriBuilder,
            id,
            participantId,
            this.activeCalls,
            Call
        );
    },

    forceTransfer: function (callId, participantId, options) {
        return apiCore.v2.post(
            callService.uriBuilder.replace(callId, participantId),
            options
        );
    },

    saveAfterCallWork: function (callId, participantId, data) {
        return apiCore.v2.patch(
            this.uriBuilder.participant(callId, participantId),
            data
        );
    },

    /**
     * Coach a participant on a call
     * @param {string} id the id of the call to coach.
     * @param {string} participantId the id of the participant to coach.
     * @param {Boolean} notifySubscribers indicates if subscribes to new answered calls should be notified
     *                  of the newly coached call.
     *
     * @return {promise} a promise that resolves with the {@link Call} object
     */
    coachCall: function (id, participantId, notifySubscribers) {
        const self = this;

        // This is a call that the current user is currently not a participant of.  So, we have to request
        // to be added as a coach and then wait for the new call to show up in the active calls list, and then
        // verify that the current user's coach attribute is set to true.
        return apiCore.v2
            .post(callService.uriBuilder.coach(id, participantId), null, null)
            .then(function () {
                return waitForNewCallToBeCreated(id, CALL_TYPES.COACH);
            })
            .then(function (call) {
                return communicationService.validateActionSucceeded(
                    call.id,
                    call.participantForCurrentUser().id,
                    Call,
                    "status",
                    participantStatus.CONNECTED,
                    self.activeCalls,
                    self.uriBuilder
                );
            })
            .then(function (call) {
                if (notifySubscribers) {
                    PubSub.Topic(MAKE_CALL_NOTIFICATION_TOPIC).publish(id);
                }
                return call;
            })
            .catch(function (err) {
                LOG.error("Error while coaching call:", err);
                if (
                    err.details &&
                    err.details.response &&
                    err.details.response.code
                ) {
                    toasts.notifyMajorAlert(
                        "phone.coachCall.error." + err.details.response.code
                    );
                } else {
                    toasts.notifyMajorAlert("phone.coachCall.error.generic");
                }
            });
    },

    /**
     * BargedIn to a call
     * @param {string} id the id of the call to bargeIn.
     * @param {string} participantId the id of the participant to bargeIn.
     * @param {Boolean} notifySubscribers indicates if subscribes to new answered calls should be notified
     *                  of the newly BargedIn call.
     *
     * @return {promise} a promise that resolves with the {@link Call} object
     */
    bargeInCall: function (id, participantId, notifySubscribers) {
        const self = this;

        // This is a call that the current user is currently not a participant of.  So, we have to request
        // to be added as a bargeIn and then wait for the new call to show up in the active calls list, and then
        // verify that the current user's bargeIn attribute is set to true.
        return apiCore.v2
            .post(callService.uriBuilder.bargeIn(id, participantId), null, null)
            .then(function () {
                return waitForNewCallToBeCreated(id, CALL_TYPES.BARGE);
            })
            .then(function (call) {
                return communicationService.validateActionSucceeded(
                    call.id,
                    call.participantForCurrentUser().id,
                    Call,
                    "status",
                    participantStatus.CONNECTED,
                    self.activeCalls,
                    self.uriBuilder
                );
            })
            .then(function (call) {
                if (notifySubscribers) {
                    PubSub.Topic(MAKE_CALL_NOTIFICATION_TOPIC).publish(id);
                }
                return call;
            })
            .catch(function (err) {
                LOG.error("Error while bargeIn call:", err);
                if (
                    err.details &&
                    err.details.response &&
                    err.details.response.code
                ) {
                    toasts.notifyMajorAlert(
                        "phone.bargeInCall.error." + err.details.response.code
                    );
                } else {
                    toasts.notifyMajorAlert("phone.bargeInCall.error.generic");
                }
            });
    },
};

communicationService.subscribeToTopic(
    "conversations.calls",
    callService.activeCalls,
    callService.getActiveCalls,
    Call
);

/**
 * Handler for  new calls that are added to a user's live
 * call data.
 */
const onNewUserCall = function (call) {
    LOG.debug("Call state tracked in active calls", call.id);
};

/**
 * Pushes call data into active calls, replacing any missing data,
 * but never overwriting existing data from push events. This
 * behavior ensures eventual consistency, provided that the
 * callData is fetched after activeCalls becomes live.
 *
 * @param {object} callData raw call data to push to active calls
 */
const fillToActiveCalls = function (callData) {
    let activeCalls = callService.activeCalls,
        call = _.find(activeCalls(), { id: callData.id });
    if (call) {
        LOG.debug("Filling data into active calls", callData);
        Call.fill(call, callData);
    } else {
        LOG.debug("Adding call to activeCalls", callData);
        call = addToActiveCalls(callData);
    }
    return call;
};

/**
 * Adds a new call object to the list of active calls based on
 * the provided call data, and sets up tracking so the call
 * will be remove when the user disconnects.
 * @param {object} callData raw call data to add to active calls
 */
const addToActiveCalls = function (callData) {
    const call = new Call(callData);
    callService.activeCalls.push(call);
    onNewUserCall(call);
    return call;
};

const waitForNewCallToBeCreated = function (callId, waitingFor = "") {
    let resolvePromise, rejectPromise;
    const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
    });
    const call = _.find(activeCalls(), { id: callId });

    if (call && call.participantForCurrentUser()) {
        if (
            call.participantForCurrentUser().coachedParticipantId() !== null ||
            call.participantForCurrentUser().monitoredParticipantId() !==
                null ||
            call.participantForCurrentUser().bargedParticipantId() !== null
        ) {
            const getActiveCallsWithMorphedParticipantId = () =>
                callService.getActiveCalls({
                    shouldReturnRawData: true,
                });

            pollFunction(getActiveCallsWithMorphedParticipantId);
        } else {
            resolvePromise(call);
        }
        return promise;
    }

    function pollFunction(callback) {
        return withRequestRetry(
            callback,
            function (callData) {
                const entity = _.find(callData.entities, function (entity) {
                    return entity.id == callId;
                });
                const {
                    monitoredParticipantId,
                    coachedParticipantId,
                    bargedParticipantId,
                    mediaRoles,
                } = _.find(entity.participants, function (participant) {
                    return (
                        participant.purpose === "user" &&
                        call.participantForCurrentUser().userId() ==
                            participant.user.id &&
                        !participant.endTime
                    );
                });

                //Uisng waitingFor we know which supervisoer requested to Monitor / Coach / Barge and
                //we will retry until we get monitor/coach/bagrge participant ids available in api

                if (
                    (waitingFor == CALL_TYPES.MONITOR &&
                        monitoredParticipantId) ||
                    (waitingFor == CALL_TYPES.COACH && coachedParticipantId) ||
                    (waitingFor == CALL_TYPES.BARGE && bargedParticipantId) ||
                    waitingFor == ""
                ) {
                    call.participantForCurrentUser().monitoredParticipantId(
                        monitoredParticipantId
                    );
                    call.participantForCurrentUser().coachedParticipantId(
                        coachedParticipantId
                    );
                    call.participantForCurrentUser().bargedParticipantId(
                        bargedParticipantId
                    );
                    call.participantForCurrentUser().isSupervisorBarged(
                        mediaRoles && mediaRoles.includes("barge")
                    );
                    resolvePromise(call);
                    return;
                }
                return true;
            },
            function (error) {
                rejectPromise(error);
                errorCallbackFactory.createCallback({
                    namespace: "conversationDetails.call.error",
                })(error);
            },
            { maxRetries: MAX_RETRIES, startIntervalMs: 1000 }
        )();
    }

    const pollingObj = {
        timeout: null,
        xhrRequest: null,
    };

    const subscription = callService.activeCalls.subscribe(function () {
        const call = _.find(activeCalls(), { id: callId });

        if (call) {
            resolvePromise(call);

            if (pollingObj.timeout) {
                clearTimeout(pollingObj.timeout);
            }

            if (pollingObj.xhrRequest) {
                pollingObj.xhrRequest.cancel();
            }

            subscription.dispose();
        }
    });

    let tries = 0;
    let callIsDisconnected = false;

    const getCallTimeout = function () {
        pollingObj.xhrRequest = callService.getCall(callId, {
            shouldReturnRawData: true,
        });

        pollingObj.xhrRequest
            .then(function (callData) {
                if (_.has(callData, "participants")) {
                    if (subscription) {
                        // get rid of the subscription on success becuase we
                        // just got the latest data from the backend.
                        subscription.dispose();
                    }

                    let call = new Call(callData);
                    // only add in the call if it is active.
                    if (!call.isDisconnected()) {
                        call = fillToActiveCalls(callData);
                    }
                    callIsDisconnected = call.isDisconnected();
                    resolvePromise(call);
                }
            })
            .catch(Promise.CancellationError, _.noop)
            .finally(function () {
                const call = _.find(activeCalls(), { id: callId });

                // if the call is found or is not active then don't try anymore
                if (call || callIsDisconnected) {
                    if (subscription) {
                        subscription.dispose();
                    }
                    pollingObj.xhrRequest = null;
                } else {
                    if (tries > 15) {
                        // stop trying to get the call's data after 15 seconds as there
                        // was most likely an error in creating the call. This number
                        // came from Anthony Roach.
                        if (subscription) {
                            subscription.dispose();
                        }
                        rejectPromise(
                            new Error("Timeout waiting for call creation")
                        );
                    } else {
                        // The call has not been fully created yet, so try again
                        pollingObj.timeout = setTimeout(getCallTimeout, 1000);
                        tries++;
                    }
                }
            });
    };

    // Poll for the new call after waiting for 15s
    pollingObj.timeout = setTimeout(getCallTimeout, 1000);

    return promise;
};

ko.computed(function () {
    callService.activeCalls(
        _.filter(callService.activeCalls(), function (call) {
            if (_.has(call, "active")) {
                return call.active();
            }

            return false;
        })
    );
});

export default callService;
